import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  CalendarCheck,
  MagnifyingGlass,
  ArrowUpRight,
  Funnel,
  Wrench,
  XCircle,
  Check,
  Eye,
  Phone,
  Envelope,
  DeviceMobile,
  Calendar,
  Note,
  X,
  Tag,
  Trash,
} from "@phosphor-icons/react";
import DateRangeFilter, { isInRange } from "../components/DateRangeFilter";
import ConfirmBookingModal from "../components/ConfirmBookingModal";
import PinPromptModal from "../components/PinPromptModal";

function FilterPill({ label, count, active, onClick, dotColor, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 h-8 pl-3 pr-2.5 rounded-full border text-[12px] font-semibold tracking-[0.02em] transition-all shrink-0 ${
        active
          ? "bg-[#121216] border-[#121216] text-white shadow-sm"
          : "bg-white border-[#E5E7EB] text-[#4B5563] hover:border-[#121216] hover:text-[#121216]"
      }`}
    >
      {dotColor && (
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: active ? "#ffffff" : dotColor }}
        />
      )}
      <span>{label}</span>
      <span
        className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold tabular-nums ${
          active ? "bg-white/20 text-white" : "bg-[#F3F4F6] text-[#6B7280]"
        }`}
      >
        {count ?? 0}
      </span>
    </button>
  );
}

const STATUS_ORDER = ["new", "confirmed", "converted", "cancelled"];
const STATUS_LABELS = {
  new: "New",
  confirmed: "Confirmed",
  converted: "Converted",
  cancelled: "Cancelled",
};
const STATUS_COLORS = {
  new: "#1E5FEB",
  confirmed: "#C98B24",
  converted: "#2F8F6E",
  cancelled: "#9CA3AF",
};

/**
 * Inbound bookings from the external booking website (see Settings → Integrations).
 * Bookings are cross-store (like customers). Staff convert them into repair tickets
 * on drop-off; the resulting ticket lands in the currently-active store.
 */
export default function Bookings() {
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [q, setQ] = useState("");
  const [activeStatus, setActiveStatus] = useState("all");
  const [dateRange, setDateRange] = useState({ mode: "all", from: null, to: null });
  const [confirmingBooking, setConfirmingBooking] = useState(null);
  const [viewing, setViewing] = useState(null);
  // Hard-delete state — admin-only. We collect the booking(s) to delete
  // in `pendingDelete` and then surface the shared PinPromptModal so the
  // admin must enter their PIN before the booking is permanently removed.
  const [pendingDelete, setPendingDelete] = useState(null); // {ids: [], label: ""} | null
  const [selected, setSelected] = useState({});             // {bookingId: true}

  const load = () => {
    const params = {};
    if (q) params.q = q;
    api.get("/bookings", { params }).then((r) => setBookings(r.data));
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  // Filter by the customer's PREFERRED time (when they actually want the
  // repair), not the booked-at timestamp. Bookings with no preferred_at
  // (e.g. quick walk-in entries) fall back to created_at so the row
  // still appears in date-bounded views — otherwise the user would lose
  // visibility of those when filtering by "this week".
  const inDate = (b) => isInRange(b.preferred_at || b.created_at, dateRange);

  const counts = useMemo(() => {
    const c = { all: 0 };
    STATUS_ORDER.forEach((s) => { c[s] = 0; });
    bookings.forEach((b) => {
      if (!inDate(b)) return;
      c.all++;
      if (c[b.status] !== undefined) c[b.status]++;
    });
    return c;
    // eslint-disable-next-line
  }, [bookings, dateRange]);

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      if (!inDate(b)) return false;
      if (activeStatus !== "all" && b.status !== activeStatus) return false;
      return true;
    });
    // eslint-disable-next-line
  }, [bookings, activeStatus, dateRange]);

  const convert = (b) => {
    // Open the Confirm Booking modal (captures price + warranty, then prints)
    setConfirmingBooking(b);
  };

  const cancel = async (b) => {
    if (!window.confirm(`Cancel booking for ${b.customer_name}?`)) return;
    try {
      await api.patch(`/bookings/${b.id}`, { status: "cancelled" });
      toast.success("Booking cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirm = async (b) => {
    try {
      await api.patch(`/bookings/${b.id}`, { status: "confirmed" });
      toast.success("Booking confirmed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Hard-delete handlers — open the PIN modal, then on confirm hit the
  // admin-only DELETE/bulk-delete endpoint. Selection state is cleared on
  // success so the row doesn't linger as "selected but already gone".
  const askDelete = (b) => setPendingDelete({ ids: [b.id], label: b.customer_name || "booking" });
  const askBulkDelete = () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return;
    setPendingDelete({ ids, label: `${ids.length} bookings` });
  };
  const performDelete = async (pin) => {
    const ids = pendingDelete?.ids || [];
    if (ids.length === 0) return;
    if (ids.length === 1) {
      await api.delete(`/bookings/${ids[0]}`, { headers: { "X-Admin-Pin": pin } });
    } else {
      await api.post("/bookings/bulk-delete", { booking_ids: ids },
                     { headers: { "X-Admin-Pin": pin } });
    }
    toast.success(`Deleted ${ids.length} booking${ids.length === 1 ? "" : "s"}`);
    setSelected({});
    setPendingDelete(null);
    load();
  };

  const toggleSelected = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  return (
    <Layout>
      <PageHeader
        overline="BOOKINGS"
        title="Incoming bookings"
        actions={
          <>
            <DateRangeFilter value={dateRange} onChange={setDateRange} />
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input
                data-testid="bookings-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search customer / device / problem"
                className="w-80 h-11 pl-10 pr-4 border border-[#E5E7EB] bg-white focus:border-[#121216] focus:outline-none text-[13px] rounded-full placeholder:text-[#9CA3AF] transition-colors"
              />
            </div>
          </>
        }
      />

      <div className="px-4 sm:px-6 md:px-8 pb-10 pt-2">
        <div className="space-y-4">
          {/* Horizontal filter bar — replaces 240px sidebar to reclaim table width on small screens */}
          <div className="flex items-center gap-2 overflow-x-auto -mx-2 px-2 pb-1">
            <div className="flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase text-[#9CA3AF] font-semibold shrink-0">
              <Funnel size={12} weight="duotone" /> FILTER
            </div>
            <FilterPill label="All" count={counts.all} active={activeStatus === "all"} onClick={() => setActiveStatus("all")} testid="booking-filter-all" />
            {STATUS_ORDER.map((s) => (
              <FilterPill
                key={s}
                label={STATUS_LABELS[s]}
                count={counts[s]}
                dotColor={STATUS_COLORS[s]}
                active={activeStatus === s}
                onClick={() => setActiveStatus(s)}
                testid={`booking-filter-${s}`}
              />
            ))}
          </div>

          {/* List */}
          <section className="rounded-3xl border border-[#E5E7EB] bg-white overflow-hidden" style={{ boxShadow: "0 1px 0 rgba(17, 17, 17, 0.02)" }}>
            {/* Desktop header */}
            <div className="hidden md:grid grid-cols-[1.4fr_1.3fr_1fr_0.9fr_90px_100px_180px] gap-4 px-6 py-4 border-b border-[#F3F4F6] text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
              <div>Customer</div>
              <div>Device & problem</div>
              <div>Preferred time</div>
              <div>Store</div>
              <div className="text-right">Price</div>
              <div>Status</div>
              <div className="text-right">Actions</div>
            </div>

            {filtered.length === 0 ? (
              <div className="p-16 text-center">
                <CalendarCheck size={36} weight="duotone" className="mx-auto text-[#9CA3AF] mb-2" />
                <div className="text-sm text-[#4B5563]">No bookings match.</div>
                <div className="text-[11px] text-[#9CA3AF] mt-1">
                  Incoming bookings from your website appear here. See <Link to="/settings" className="underline">Settings → Integrations</Link> for the endpoint.
                </div>
              </div>
            ) : (
              <ul className="divide-y divide-[#F3F4F6]">
                {filtered.map((b) => (
                  <li key={b.id}>
                    {/* Desktop row */}
                    <div className="hidden md:grid grid-cols-[1.4fr_1.3fr_1fr_0.9fr_90px_100px_180px] gap-4 px-6 py-4 items-center hover:bg-[#F8FAFC] transition-colors" data-testid={`booking-row-${b.id}`}>
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-[#121216] truncate flex items-center gap-2">
                          {b.customer_name}
                          {b.external_ref && (
                            <span className="text-[9px] font-mono tracking-[0.08em] uppercase px-1.5 py-0.5 rounded bg-[#F8FAFC] border border-[#E5E7EB] text-[#4B5563]" title="Booking website reference">
                              {b.external_ref}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] font-mono text-[#9CA3AF] mt-0.5 truncate">
                          {[b.customer_phone, b.customer_email].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-[#121216] truncate">
                          {b.device_make} {b.device_model}
                          {b.device_year && (
                            <span className="ml-1.5 text-[11px] font-normal text-[#6B7280]">({b.device_year})</span>
                          )}
                        </div>
                        {b.device_model_number && (
                          <div className="text-[10.5px] font-mono text-[#6B7280] uppercase tracking-wider mt-0.5">
                            {b.device_model_number}
                          </div>
                        )}
                        <div className="text-[12px] text-[#4B5563] truncate mt-0.5 flex items-center gap-1.5">
                          <span className="truncate">{b.problem || "—"}</span>
                          {b.part_option && (
                            <span
                              className="shrink-0 inline-flex items-center gap-1 text-[9px] font-bold tracking-[0.12em] uppercase px-1.5 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E5FEB] border border-[#BFDBFE]"
                              title="Part option chosen by customer on website"
                              data-testid={`booking-part-${b.id}`}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-[#1E5FEB]" />
                              {b.part_option}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-[12px] text-[#4B5563] font-mono flex flex-col gap-1 items-start">
                        <span>
                          {b.preferred_at ? formatPreferred(b.preferred_at) : <span className="text-[#9CA3AF]">Not set</span>}
                        </span>
                        {b.website_update_pending && (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] font-bold tracking-[0.12em] uppercase px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]"
                            title="Customer changed booking details on the website after staff had actioned it. Review before the customer arrives."
                            data-testid={`booking-website-update-${b.id}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-[#B45309] animate-pulse" />
                            Customer updated
                          </span>
                        )}
                        {b.website_cancellation_pending && (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] font-bold tracking-[0.12em] uppercase px-1.5 py-0.5 rounded-full bg-[#FEE2E2] text-[#B91C1C] border border-[#FECACA]"
                            title="Customer requested cancellation on the website after this was converted to a ticket. Call the customer to confirm."
                            data-testid={`booking-website-cancel-${b.id}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-[#B91C1C] animate-pulse" />
                            Cancel requested
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-[#4B5563] truncate">{b.store_name || "—"}</div>
                      <div className="text-right">
                        {Number(b.estimated_cost) > 0 ? (
                          <span className="text-[13px] font-mono font-bold text-[#121216]">${Number(b.estimated_cost).toFixed(2)}</span>
                        ) : (
                          <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">NO PRICE</span>
                        )}
                      </div>
                      <div><StatusPill status={b.status} /></div>
                      <div className="flex items-center justify-end gap-1.5">
                        <IconButton title="View details" testid={`view-${b.id}`} onClick={() => setViewing(b)}>
                          <Eye size={12} weight="bold" />
                        </IconButton>
                        {b.status === "new" && (
                          <IconButton title="Confirm" testid={`confirm-${b.id}`} onClick={() => confirm(b)}>
                            <Check size={12} weight="bold" />
                          </IconButton>
                        )}
                        {(b.status === "new" || b.status === "confirmed") && (
                          <button
                            onClick={() => convert(b)}
                            data-testid={`convert-${b.id}`}
                            className="h-7 px-3 rounded-full bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[11px] font-semibold flex items-center gap-1 transition-colors"
                          >
                            <Wrench size={11} weight="bold" /> Convert
                          </button>
                        )}
                        {b.status === "converted" && b.repair_ticket_id && (
                          <Link
                            to={`/repairs/${b.repair_ticket_id}`}
                            data-testid={`open-ticket-${b.id}`}
                            className="h-7 px-3 rounded-full border border-[#E5E7EB] hover:border-[#121216] text-[11px] font-semibold flex items-center gap-1 text-[#4B5563] hover:text-[#121216] transition-colors"
                          >
                            Open ticket <ArrowUpRight size={11} weight="bold" />
                          </Link>
                        )}
                        {(b.status === "new" || b.status === "confirmed") && (
                          <IconButton title="Cancel" testid={`cancel-${b.id}`} onClick={() => cancel(b)} danger>
                            <XCircle size={12} weight="bold" />
                          </IconButton>
                        )}
                        <IconButton
                          title="Delete permanently (admin)"
                          testid={`delete-${b.id}`}
                          onClick={() => askDelete(b)}
                          danger
                        >
                          <Trash size={12} weight="bold" />
                        </IconButton>
                      </div>
                    </div>
                    {/* Mobile card */}
                    <div className="md:hidden px-4 py-3.5 hover:bg-[#F8FAFC] active:bg-[#F0F4FF] transition-colors" data-testid={`booking-mobile-${b.id}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="text-[14px] font-semibold text-[#121216] truncate">{b.customer_name}</div>
                        <StatusPill status={b.status} />
                      </div>
                      <div className="text-[12px] text-[#4B5563] truncate">
                        {b.device_make} {b.device_model}{b.problem ? ` — ${b.problem}` : ""}
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="text-[11px] font-mono text-[#9CA3AF]">
                          {b.preferred_at ? formatPreferred(b.preferred_at) : "No time set"}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {Number(b.estimated_cost) > 0 ? (
                            <span className="text-[12px] font-mono font-bold text-[#121216]">${Number(b.estimated_cost).toFixed(2)}</span>
                          ) : (
                            <span className="text-[9px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">NO PRICE</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 mt-2">
                        <IconButton title="View" testid={`view-m-${b.id}`} onClick={() => setViewing(b)}>
                          <Eye size={12} weight="bold" />
                        </IconButton>
                        {(b.status === "new" || b.status === "confirmed") && (
                          <button
                            onClick={() => convert(b)}
                            data-testid={`convert-m-${b.id}`}
                            className="h-7 px-3 rounded-full bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[11px] font-semibold flex items-center gap-1 transition-colors"
                          >
                            <Wrench size={11} weight="bold" /> Convert
                          </button>
                        )}
                        {b.status === "converted" && b.repair_ticket_id && (
                          <Link
                            to={`/repairs/${b.repair_ticket_id}`}
                            data-testid={`open-ticket-m-${b.id}`}
                            className="h-7 px-3 rounded-full border border-[#E5E7EB] hover:border-[#121216] text-[11px] font-semibold flex items-center gap-1 text-[#4B5563] hover:text-[#121216] transition-colors"
                          >
                            Open ticket <ArrowUpRight size={11} weight="bold" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {filtered.length > 0 && (
              <div className="px-6 py-3 border-t border-[#F3F4F6] text-[11px] text-[#9CA3AF] font-mono">
                {filtered.length} {filtered.length === 1 ? "booking" : "bookings"}
              </div>
            )}
          </section>
        </div>
      </div>
      <ConfirmBookingModal
        open={!!confirmingBooking}
        booking={confirmingBooking}
        onClose={() => setConfirmingBooking(null)}
        onConverted={() => { setConfirmingBooking(null); load(); }}
      />
      <BookingDetailsModal
        booking={viewing}
        onClose={() => setViewing(null)}
        onConvert={(b) => { setViewing(null); setConfirmingBooking(b); }}
        onAcknowledged={() => load()}
      />
      <PinPromptModal
        open={!!pendingDelete}
        title={pendingDelete?.ids.length === 1 ? "Delete booking?" : `Delete ${pendingDelete?.ids.length} bookings?`}
        description={
          pendingDelete?.ids.length === 1
            ? `Permanently delete the booking for ${pendingDelete?.label}. This cannot be undone.`
            : `Permanently delete ${pendingDelete?.label}. This cannot be undone.`
        }
        confirmLabel="DELETE"
        destructive
        onClose={() => setPendingDelete(null)}
        onConfirm={performDelete}
      />
    </Layout>
  );
}

function formatPreferred(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return `${date} · ${time}`;
  } catch { return iso; }
}

function FilterItem({ label, count, dotColor, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-colors shrink-0 ${
        active ? "bg-[#121216] text-white" : "text-[#4B5563] hover:bg-[#F8FAFC] hover:text-[#121216]"
      }`}
    >
      {dotColor ? (
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: active ? "#FFFFFF" : dotColor }} />
      ) : (
        <span className="w-1.5 h-1.5 shrink-0" />
      )}
      <span className="flex-1 text-left font-medium whitespace-nowrap">{label}</span>
      <span className={`text-[11px] tabular-nums font-mono ${active ? "text-white/60" : "text-[#9CA3AF]"}`}>{count}</span>
    </button>
  );
}

function StatusPill({ status }) {
  const color = STATUS_COLORS[status] || STATUS_COLORS.new;
  return (
    <span
      className="text-[10px] tracking-[0.06em] uppercase px-2.5 py-1 rounded-full font-semibold inline-block"
      style={{ background: `${color}15`, color }}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function IconButton({ children, onClick, title, testid, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`w-7 h-7 rounded-full border flex items-center justify-center transition-colors ${
        danger
          ? "border-[#E5E7EB] text-[#4B5563] hover:border-[#DC2626] hover:text-[#DC2626]"
          : "border-[#E5E7EB] text-[#4B5563] hover:border-[#121216] hover:text-[#121216] hover:bg-[#F8FAFC]"
      }`}
    >
      {children}
    </button>
  );
}

function BookingDetailsModal({ booking, onClose, onConvert, onAcknowledged }) {
  if (!booking) return null;
  const when = booking.preferred_at
    ? new Date(booking.preferred_at).toLocaleString("en-AU", { dateStyle: "full", timeStyle: "short" })
    : "Not specified";
  const created = booking.created_at
    ? new Date(booking.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })
    : "";
  const price = Number(booking.estimated_cost || 0);
  const hasWebsiteUpdate = booking.website_update_pending || booking.website_cancellation_pending;

  const acknowledge = async () => {
    try {
      await api.post(`/bookings/${booking.id}/acknowledge-website-update`);
      toast.success("Marked as reviewed");
      onAcknowledged?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Could not acknowledge");
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="booking-details-modal"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-7 py-5 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#1E5FEB] font-bold">
              BOOKING DETAILS{booking.external_ref && ` · ${booking.external_ref}`}
            </div>
            <div className="text-[22px] font-bold text-[#121216] leading-tight mt-1">
              {booking.customer_name}
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">
              Received {created} · <span className="uppercase tracking-wider">{booking.source || "website"}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
            data-testid="booking-details-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Website update / cancellation alert — shows when the customer
            changed their booking on the website AFTER staff had actioned it. */}
        {hasWebsiteUpdate && (
          <div
            className={`px-7 py-4 border-b ${
              booking.website_cancellation_pending
                ? "bg-[#FEE2E2] border-[#FECACA]"
                : "bg-[#FEF3C7] border-[#FDE68A]"
            }`}
            data-testid="booking-website-alert"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className={`text-[10px] uppercase tracking-[0.16em] font-bold mb-1 ${
                  booking.website_cancellation_pending ? "text-[#B91C1C]" : "text-[#92400E]"
                }`}>
                  {booking.website_cancellation_pending
                    ? "⚠️ CANCELLATION REQUESTED ON WEBSITE"
                    : "⚠️ CUSTOMER UPDATED BOOKING ON WEBSITE"}
                </div>
                <div className={`text-[12px] leading-relaxed ${
                  booking.website_cancellation_pending ? "text-[#7F1D1D]" : "text-[#78350F]"
                }`}>
                  {booking.website_cancellation_pending
                    ? "The customer requested cancellation on the website after this booking was converted to a ticket. Call the customer to confirm whether the repair should proceed."
                    : "The customer changed details on the website after staff had already actioned this booking. Review the notes below and update the ticket accordingly if needed."}
                </div>
                {booking.website_cancellation_reason && (
                  <div className="mt-2 text-[11px] font-mono text-[#7F1D1D]">
                    Reason: {booking.website_cancellation_reason}
                  </div>
                )}
              </div>
              <button
                onClick={acknowledge}
                className={`h-9 px-4 rounded-lg text-[11px] font-bold tracking-[0.12em] uppercase text-white ${
                  booking.website_cancellation_pending
                    ? "bg-[#B91C1C] hover:bg-[#991B1B]"
                    : "bg-[#B45309] hover:bg-[#92400E]"
                }`}
                data-testid="booking-acknowledge-btn"
              >
                Mark reviewed
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {/* Customer + timing */}
          <div className="p-6 space-y-3 bg-slate-50/50 border-r border-slate-100">            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">CONTACT</div>
            {booking.customer_phone && (
              <div className="flex items-center gap-2 text-[13px] text-slate-700 font-mono">
                <Phone size={13} weight="bold" className="text-slate-400" /> {booking.customer_phone}
              </div>
            )}
            {booking.customer_email && (
              <div className="flex items-center gap-2 text-[13px] text-slate-700">
                <Envelope size={13} weight="bold" className="text-slate-400" /> {booking.customer_email}
              </div>
            )}
            <div className="pt-3 border-t border-slate-200/70">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">PREFERRED TIME</div>
              <div className="flex items-start gap-2 text-[13px] text-slate-700">
                <Calendar size={13} weight="bold" className="mt-1 text-slate-400" />
                <span className="leading-relaxed">{when}</span>
              </div>
            </div>
            <div className="pt-3 border-t border-slate-200/70">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">STORE</div>
              <div className="text-[13px] text-slate-700">{booking.store_name || "—"}</div>
            </div>
          </div>

          {/* Device + problem + price */}
          <div className="p-6 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">DEVICE</div>
            <div className="flex items-start gap-2">
              <DeviceMobile size={16} weight="bold" className="mt-1 text-slate-400" />
              <div>
                <div className="text-[15px] font-bold text-[#121216]">
                  {[booking.device_make, booking.device_model].filter(Boolean).join(" ") || "— not specified —"}
                  {booking.device_year && (
                    <span className="ml-2 text-[12px] font-normal text-slate-500">({booking.device_year})</span>
                  )}
                </div>
                {booking.device_model_number && (
                  <div className="text-[11px] font-mono tracking-wider uppercase text-slate-500 mt-0.5">
                    Model {booking.device_model_number}
                  </div>
                )}
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">PROBLEM / PART</div>
              <div className="text-[13px] text-slate-700 leading-relaxed bg-slate-50 border border-slate-200 rounded-xl p-3 whitespace-pre-wrap">
                {booking.problem || "— nothing recorded —"}
              </div>
              {booking.part_option && (
                <div className="mt-2 flex items-center gap-2 text-[12px]">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-bold">Part option:</span>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full bg-[#DBEAFE] text-[#1E5FEB] border border-[#BFDBFE]">
                    <span className="w-2 h-2 rounded-full bg-[#1E5FEB]" />
                    {booking.part_option}
                  </span>
                  <span className="text-[11px] text-slate-400">chosen on website</span>
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">BOOKED PRICE</div>
              {price > 0 ? (
                <div className="flex items-baseline gap-2">
                  <Tag size={15} weight="bold" className="text-[#1E5FEB]" />
                  <span className="text-[22px] font-bold font-mono text-[#121216]">${price.toFixed(2)}</span>
                  <span className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">from website</span>
                </div>
              ) : (
                <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-relaxed">
                  No price was set on the website booking form. Staff will set the agreed estimate on convert.
                </div>
              )}
            </div>

            {booking.notes && (
              <div className="pt-3 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">CUSTOMER NOTES</div>
                <div className="flex items-start gap-2 text-[13px] text-slate-700 italic leading-relaxed">
                  <Note size={13} weight="bold" className="mt-1 text-slate-400" />
                  <span>"{booking.notes}"</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-7 py-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between">
          <button
            onClick={onClose}
            className="text-[12px] font-bold tracking-wider text-slate-500 hover:text-slate-800"
            data-testid="booking-details-close-btn"
          >
            CLOSE
          </button>
          {(booking.status === "new" || booking.status === "confirmed") && (
            <button
              onClick={() => onConvert?.(booking)}
              data-testid="booking-details-convert"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[12px] font-bold tracking-[0.15em] transition-colors"
            >
              <Wrench size={13} weight="bold" /> CONVERT TO TICKET
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

